import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import TaskPackage from "../model/TaskPackage";
import TreeItemPackage from "../dragdrop/TreeItemPackage";
import TreeItemTask from "../dragdrop/TreeItemTask";
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react";
import { useProjectStore } from "../model/ProjectStore";
import { useViewState } from "../model/ViewState";

//  ----------------------------- Drag and Drop Tree Logic -----------------------------
const DragTreeItem = (taskPackage, view, movePackage, moveTask) => {
  const packages = taskPackage.taskPackages.map((pack) => {
    return DragTreeItem(pack, view, movePackage, moveTask);
  });
  const tasks = taskPackage.tasks.map((task) => {
    return (
      <TreeItemTask
        parent={taskPackage}
        key={task.id}
        id={task.id}
        task={task}
        moveTask={moveTask}
      />
    );
  });

  let children = packages.concat(tasks);

  // sort the tasks and categories
  children = children.sort(function (a, b) {
    let aIndex =
      a.props.pack === undefined
        ? a.props.task.displayIndex
        : a.props.pack.displayIndex;
    let bIndex =
      b.props.pack === undefined
        ? b.props.task.displayIndex
        : b.props.pack.displayIndex;
    if (parseInt(aIndex) >= parseInt(bIndex)) {
      return 1;
    } else {
      return -1;
    }
  });

  return (
    <TreeItemPackage
      key={taskPackage.id}
      pack={taskPackage}
      children={children}
      movePackage={movePackage}
    />
  );
};

const generateTaskTreeItems = (
  taskPackage,
  view,
  movePackage,
  moveTask
) => {
  let children = taskPackage.taskPackages.map((childPackage) => {
    return DragTreeItem(childPackage, view, movePackage, moveTask);
  });

  const tasks = taskPackage.tasks.map((task) => {
    return (
      <TreeItemTask
        parent={taskPackage}
        key={task.id}
        id={task.id}
        task={task}
        moveTask={moveTask}
      />
    );
  });

  children = children.concat(tasks);

  // sort the tasks and categories
  children = children.sort(function (a, b) {
    let aIndex =
      a.props.pack === undefined
        ? a.props.task.displayIndex
        : a.props.pack.displayIndex;
    let bIndex =
      b.props.pack === undefined
        ? b.props.task.displayIndex
        : b.props.pack.displayIndex;

    if (parseInt(aIndex) >= parseInt(bIndex)) {
      return 1;
    } else {
      return -1;
    }
  });
  const label = taskPackage.code + " " + taskPackage.name;

  return (
    <TreeItem
      itemId={taskPackage.id}
      label={
        <Typography variant="body1" style={{ fontWeight: "normal" }}>
          {label}
        </Typography>
      }
      onClick={(event) => {
        if (event.target.closest('.MuiTreeItem-iconContainer')) {
          return;
        }
        view.setSelectedTaskId(taskPackage.id);
        view.setSelectedTaskIndex(taskPackage.id);
      }}
    >
      {children}
    </TreeItem>
  );
};

//  ----------------------------- Expand/not expand logic  -----------------------------
const updatePanel = (event, id, view, click) => {
  // let child = event._targetInst.pendingProps.children;
  // if (typeof child == "string") {
  //   click(id);
  // }
  const isIconClick = (event.target).closest(".MuiTreeItem-iconContainer");
  if (isIconClick) {
    event.stopPropagation();
    return;
  }
  click(id);
};

const UpdateExpand = (event, expanded, view, fromTask) => {
  let child = event._targetInst.pendingProps.children;
  if (typeof child != "string") {
    if (fromTask) {
      view.lastTaskTreeExpansion = expanded;
    } else {
      view.lastAnalysisTreeExpansion = expanded;
    }
  }
};

// ----------------------------- Insert Logic -----------------------------

const getDragHover = (dragIndex, hoverIndex, root) => {
  let drag = root.getTaskDND(dragIndex);
  let hover = root.getTaskDND(hoverIndex);

  if (drag === null) {
    drag = root.getTaskPackageDND(dragIndex);
  }

  if (hover === null) {
    hover = root.getTaskPackageDND(hoverIndex);
  }

  return {
    drag: drag,
    hover: hover,
  };
};

const insertTaskObject = (task, taskList) => {
  const newTask = [];
  let inserted = false;
  for (let i = 0; i < taskList.length; i++) {
    if (
      parseInt(task.displayIndex) < parseInt(taskList[i].displayIndex) &&
      !inserted
    ) {
      inserted = true;
      newTask.push(task);
    }
    newTask.push(taskList[i]);
  }
  if (newTask.length === 0 || newTask.length === taskList.length) {
    newTask.push(task);
  }
  return newTask;
};

const insertPackageObject = (pack, packages) => {
  const newPack = [];
  let inserted = false;
  for (let i = 0; i < packages.length; i++) {
    if (
      parseInt(pack.displayIndex) < parseInt(packages[i].displayIndex) &&
      !inserted
    ) {
      inserted = true;
      newPack.push(pack);
    }
    newPack.push(packages[i]);
  }
  if (newPack.length === 0 || newPack.length === packages.length) {
    newPack.push(pack);
  }
  return newPack;
};

const adjustDrag = (changeDrag, drag, parentCode, isRoot) => {
  drag.displayIndex = (parseInt(drag.displayIndex) + changeDrag).toString();

  drag.taskPackages.forEach((pack) => {
    adjustDrag(changeDrag, pack, drag.code, false);
  });

  drag.tasks.forEach((task) => {
    task.displayIndex = (parseInt(task.displayIndex) + changeDrag).toString();
  });
};

//  ----------------------------- Main Function  -----------------------------
const ProjectTreeView = ({ fromTask }) => {
  const root = useProjectStore().rootTaskPackage;
  const view = useViewState();
  let expand = fromTask
    ? view.lastTaskTreeExpansion
    : view.lastAnalysisTreeExpansion;

  const adjustExpand = (
    startIndex,
    endIndex,
    change,
    changeDrag,
    dragMax,
    dragIndex
  ) => {
    let tempExpand = expand.map((ex) => {
      if (
        parseInt(dragIndex) <= parseInt(ex) &&
        parseInt(ex) < parseInt(dragMax)
      ) {
        return (parseInt(ex) + changeDrag).toString();
      } else if (
        parseInt(startIndex) <= parseInt(ex) &&
        parseInt(ex) <= parseInt(endIndex)
      ) {
        return (parseInt(ex) + change).toString();
      } else {
        return ex;
      }
    });
    expand = tempExpand;
  };

  const moveTask = (dragIndex, hoverIndex, location, dropType) => {
    if (dragIndex === hoverIndex) {
      return;
    }

    if (location === "middle") {
      moveInPackage(dragIndex, hoverIndex, "task");
    } else if (location === "bottom" || location === "top") {
      moveTaskAdjacent(dragIndex, hoverIndex, location, dropType);
    }
  };

  const movePackage = (dragIndex, hoverIndex, location) => {
    if (dragIndex === hoverIndex) {
      return;
    }
    if (location === "middle") {
      moveInPackage(dragIndex, hoverIndex, "package");
    } else if (location === "bottom" || location === "top") {
      movePackageAdjacent(dragIndex, hoverIndex, location);
    }
  };

  const moveTaskAdjacent = (dragIndex, hoverIndex, location, dropType) => {
    const { drag, hover } = getDragHover(dragIndex, hoverIndex, root); // get drag and hover task

    drag.parent.tasks = drag.parent.tasks.filter((task) => task !== drag.task);
    let startIndex, endIndex, change, newIndex, hoverMax;

    if (dropType === "package") {
      const hoverPkg =
        hover.package !== undefined ? hover.package : hover.parent;
      hoverMax = (parseInt(hoverPkg.maxIndexPackage()) + 1).toString();
    }

    // if moving down
    if (parseInt(hoverIndex) > parseInt(dragIndex)) {
      startIndex = (parseInt(dragIndex) + 1).toString(); // start of affect ID's

      // end depends if drag is being placed below or above the hovered task
      endIndex =
        location === "top"
          ? (parseInt(hoverIndex) - 1).toString()
          : dropType === "package"
            ? (parseInt(hoverMax) - 1).toString()
            : hoverIndex;
      change = -1;
      newIndex = endIndex;
    }

    // if moving up
    else {
      // start depends if drag is being placed below or above the hovered task
      startIndex =
        location === "top"
          ? hoverIndex
          : dropType === "package"
            ? hoverMax
            : (parseInt(hoverIndex) + 1).toString();
      endIndex = (parseInt(dragIndex) - 1).toString();
      change = 1; // how much the ID's will change
      newIndex = startIndex;
    }

    // re-index tasks in the range of drag and hover
    if (view.selectedTaskIndex === dragIndex) {
      view.selectedTaskIndex = drag.task.displayIndex;
    } else if (
      parseInt(startIndex) <= parseInt(view.selectedTaskIndex) &&
      parseInt(view.selectedTaskIndex) <= parseInt(endIndex)
    ) {
      view.selectedTaskIndex = (
        parseInt(view.selectedTaskIndex) + change
      ).toString();
    }

    TaskPackage.reIndexTask(root, startIndex, endIndex, drag.task, change);

    drag.task.displayIndex = newIndex;
    drag.task.parent = hover.parent.id;
    drag.task.parentDisplayIndex = hover.parent.displayIndex;
    adjustExpand(startIndex, endIndex, change, -1, -1, -1); // adjust the expanded nodes in the tree's
    hover.parent.tasks = insertTaskObject(drag.task, hover.parent.tasks); // insert drag in the parent of hover
    TaskPackage.FixIndex(root, 1);
  };

  const movePackageAdjacent = (dragIndex, hoverIndex, location) => {
    const { drag, hover } = getDragHover(dragIndex, hoverIndex, root); // get affect categories

    // cannot move package inside itself
    if (drag.package.getTaskPackageDND(hover.parent.displayIndex) !== null) {
      return;
    }

    drag.parent.taskPackages = drag.parent.taskPackages.filter(
      (packg) => packg !== drag.package
    );

    const hoverMax = (parseInt(hover.package.maxIndexPackage()) + 1).toString(); // start of the next package not a child of the hover package
    const dragMax = (parseInt(drag.package.maxIndexPackage()) + 1).toString(); // start of the next package not a child of the drag package
    let startIndex, endIndex, change, changeDrag;

    // if moving down
    if (parseInt(hoverIndex) > parseInt(dragIndex)) {
      startIndex = dragMax; // start of affect ID's
      endIndex =
        location === "top"
          ? (parseInt(hoverIndex) - 1).toString()
          : (parseInt(hoverMax) - 1).toString(); // end of affected ID's
      change = parseInt(dragIndex) - parseInt(dragMax); // how much the ID's will change
      changeDrag = parseInt(endIndex) + change - parseInt(dragIndex) + 1; // the changed in the ID's for the Dragged package
    } else if (parseInt(dragIndex) > parseInt(hoverIndex)) {
      startIndex = location === "top" ? hoverIndex : hoverMax; // start of affect ID's
      endIndex = (parseInt(dragIndex) - 1).toString(); // end of affected ID's
      change = parseInt(dragMax) - parseInt(dragIndex); // how much the ID's will change
      changeDrag = parseInt(startIndex) - parseInt(dragIndex); // the changed in the ID's for the Dragged package
    }

    // re-index tasks in the range of drag and hover
    if (
      parseInt(dragIndex) <= parseInt(view.selectedTaskIndex) &&
      parseInt(view.selectedTaskIndex) < parseInt(dragMax)
    ) {
      view.selectedTaskIndex = (
        parseInt(view.selectedTaskIndex) + changeDrag
      ).toString();
    } else if (
      parseInt(startIndex) <= parseInt(view.selectedTaskIndex) &&
      parseInt(view.selectedTaskIndex) <= parseInt(endIndex)
    ) {
      view.selectedTaskIndex = (
        parseInt(view.selectedTaskIndex) + change
      ).toString();
    }

    TaskPackage.reIndexTask(root, startIndex, endIndex, drag.task, change);

    drag.package.parent = hover.parent.id;
    drag.package.parentDisplayIndex = hover.parent.displayIndex;
    adjustDrag(changeDrag, drag.package, drag.package.code, true); // re index the dragged package
    adjustExpand(startIndex, endIndex, change, changeDrag, dragMax, dragIndex); // adjust the expanded nodes in the tree's
    hover.parent.taskPackages = insertPackageObject(
      drag.package,
      hover.parent.taskPackages
    ); // insert drag in the parent of hover
    TaskPackage.FixIndex(root, 1);
  };

  const moveInPackage = (dragIndex, hoverIndex, dragType) => {
    const { drag, hover } = getDragHover(dragIndex, hoverIndex, root); // get affect categories

    // cannot move package inside itself
    if (dragType === "package") {
      if (drag.package.getTaskPackageDND(hover.parent.displayIndex) !== null)
        return;
    }

    const hoverMax = (parseInt(hover.package.maxIndexPackage()) + 1).toString(); // start of the next package not a child of the hover package
    const dragMax =
      dragType === "task"
        ? "-1"
        : (parseInt(drag.package.maxIndexPackage()) + 1).toString(); // start of the nextpackage not a child of the drag package if drag is a package
    let startIndex, endIndex, change, changeDrag;

    // moving down
    if (parseInt(hoverIndex) > parseInt(dragIndex)) {
      change =
        dragType === "task" ? -1 : parseInt(dragIndex) - parseInt(dragMax); // how much the ID's will change
      startIndex =
        dragType === "task" ? (parseInt(dragIndex) + 1).toString() : dragMax; // start of affect ID's
      endIndex = (parseInt(hoverMax) - 1).toString(); // end of affected ID's
      changeDrag = parseInt(hoverMax) + change - parseInt(dragIndex); // the changed in the ID's for the Dragged package
    } else if (parseInt(dragIndex) > parseInt(hoverIndex)) {
      change =
        dragType === "task" ? 1 : parseInt(dragMax) - parseInt(dragIndex); // how much the ID's will change
      startIndex = hoverMax; // start of affect ID's
      endIndex = (parseInt(dragIndex) - 1).toString(); // end of affected ID's
      changeDrag = parseInt(hoverMax) - parseInt(dragIndex); // the changed in the ID's for the Dragged package
    }

    if (dragType === "package") {
      drag.parent.taskPackages = drag.parent.taskPackages.filter(
        (packg) => packg !== drag.package
      );
      // re-index tasks in the range of drag and hover
      if (
        parseInt(dragIndex) <= parseInt(view.selectedTaskIndex) &&
        parseInt(view.selectedTaskIndex) < parseInt(dragMax)
      ) {
        view.selectedTaskIndex = (
          parseInt(view.selectedTaskIndex) + changeDrag
        ).toString();
      } else if (
        parseInt(startIndex) <= parseInt(view.selectedTaskIndex) &&
        parseInt(view.selectedTaskIndex) <= parseInt(endIndex)
      ) {
        view.selectedTaskIndex = (
          parseInt(view.selectedTaskIndex) + change
        ).toString();
      }

      TaskPackage.reIndexTask(root, startIndex, endIndex, drag.task, change);
      drag.package.parent =
        hover.package !== undefined ? hover.package.id : hover.task.id;
      drag.package.parentDisplayIndex =
        hover.package !== undefined
          ? hover.package.displayIndex
          : hover.task.displayIndex;
      adjustDrag(changeDrag, drag.package, drag.package.code, true);
      adjustExpand(
        startIndex,
        endIndex,
        change,
        changeDrag,
        dragMax,
        dragIndex
      );
      hover.package.taskPackages.push(drag.package);
    } else {
      drag.parent.tasks = drag.parent.tasks.filter(
        (task) => task !== drag.task
      );
      // re-index tasks in the range of drag and hover
      if (view.selectedTaskIndex === dragIndex) {
        view.selectedTaskIndex = hoverMax;
      } else if (
        parseInt(startIndex) <= parseInt(view.selectedTaskIndex) &&
        parseInt(view.selectedTaskIndex) <= parseInt(endIndex)
      ) {
        view.selectedTaskIndex = (
          parseInt(view.selectedTaskIndex) + change
        ).toString();
      }

      TaskPackage.reIndexTask(root, startIndex, endIndex, drag.task, change);

      drag.task.displayIndex = hoverMax;
      drag.task.parent = hover.package.id;
      drag.task.parentDisplayIndex = hover.package.displayIndex;
      adjustExpand(startIndex, endIndex, change, -1, -1, -1);

      hover.package.tasks.push(drag.task);
    }
    TaskPackage.FixIndex(root, 1);
  };
  return (
    <SimpleTreeView
      expanded={
        fromTask ? view.lastTaskTreeExpansion : view.lastAnalysisTreeExpansion
      }
      slots={{
        expandIcon: ChevronRightIcon,
        collapseIcon: ExpandMoreIcon
      }}
      expansionTrigger="iconContainer"
      
    >
      {generateTaskTreeItems(
        root,
        view,
        movePackage,
        moveTask
      )}
    </SimpleTreeView>
  );
};

export default observer(ProjectTreeView);
